import {TransportAssoc, TransportAssocEntity} from "../../../model/transport-assoc";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {createSelector} from "@reduxjs/toolkit";
import {TransportCategory} from "../../../model/transport-category";
import {TtaMappedPair, TtaMappedPairEntity} from "../../../model/tta-mapped-pair";
import {DispatchSelectorProps} from "../dispatch/selectors";

export interface TtaSelectorProps {
  regionId?: string
  category?: TransportCategory
  personId?: string
  vehicleId?: string
  trailerId?: string
  pickupSiteId?: string
  pickupSiteCompanyId?: string
  deliverySiteId?: string
  deliverySiteCompanyId?: string
  adminValidated?: boolean
  dispatchValidated?: boolean
  fieldValidated?: boolean
  businessValidated?: boolean
}

const getRegionIdParam = createParamSelector<TtaSelectorProps | undefined, string | undefined>(params => params?.regionId);
const getCategoryParam = createParamSelector<TtaSelectorProps | undefined, TransportCategory | undefined>(params => params?.category);
const getPersonIdParam = createParamSelector<TtaSelectorProps | undefined, string | undefined>(params => params?.personId);
const getVehicleIdParam = createParamSelector<TtaSelectorProps | undefined, string | undefined>(params => params?.vehicleId);
const getTrailerIdParam = createParamSelector<TtaSelectorProps | undefined, string | undefined>(params => params?.trailerId);
const getPickupSiteIdParam = createParamSelector<TtaSelectorProps | undefined, string | undefined>(params => params?.pickupSiteId);
const getPickupSiteCompanyIdParam = createParamSelector<TtaSelectorProps | undefined, string | undefined>(params => params?.pickupSiteCompanyId);
const getDeliverySiteIdParam = createParamSelector<TtaSelectorProps | undefined, string | undefined>(params => params?.deliverySiteId);
const getDeliverySiteCompanyIdParam = createParamSelector<TtaSelectorProps | undefined, string | undefined>(params => params?.deliverySiteCompanyId);
const getAdminValidatedParam = createParamSelector<TtaSelectorProps | undefined, boolean | undefined>(params => params?.adminValidated);
const getDispatchValidatedParam = createParamSelector<TtaSelectorProps | undefined, boolean | undefined>(params => params?.dispatchValidated);
const getFieldValidatedParam = createParamSelector<TtaSelectorProps | undefined, boolean | undefined>(params => params?.fieldValidated);
const getBusinessValidatedParam = createParamSelector<TtaSelectorProps | undefined, boolean | undefined>(params => params?.businessValidated);

export const ttasSelector = () => createSelector(
  [
    getUseQueryResultData<TransportAssoc[]>,
    getRegionIdParam,
    getCategoryParam,
    getPersonIdParam,
    getVehicleIdParam,
    getTrailerIdParam,
    getPickupSiteIdParam,
    getPickupSiteCompanyIdParam,
    getDeliverySiteIdParam,
    getDeliverySiteCompanyIdParam,
    getAdminValidatedParam,
    getDispatchValidatedParam,
    getFieldValidatedParam,
    getBusinessValidatedParam,
  ],
  (
    data,
    regionId,
    category,
    personId,
    vehicleId,
    trailerId,
    pickupSiteId,
    pickupSiteCompanyId,
    deliverySiteId,
    deliverySiteCompanyId,
    adminValidated,
    dispatchValidated,
    fieldValidated,
    businessValidated,
  ) => {
    return selectTtasAndTransform(
      data ?? [], {
        regionId: regionId,
        category: category,
        personId: personId,
        vehicleId: vehicleId,
        trailerId: trailerId,
        pickupSiteId: pickupSiteId,
        pickupSiteCompanyId: pickupSiteCompanyId,
        deliverySiteId: deliverySiteId,
        deliverySiteCompanyId: deliverySiteCompanyId,
        adminValidated: adminValidated,
        dispatchValidated: dispatchValidated,
        fieldValidated: fieldValidated,
        businessValidated: businessValidated,
      }
    );
  }
);

export const ttaSelector = () => createSelector(
  [getUseQueryResultData<TransportAssoc>],
  (data) => {
    return data ? transformTta(data) : undefined;
  }
);

export const selectTtas = (values?: TransportAssoc[], selector?: TtaSelectorProps): TransportAssoc[] => {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;

    if (selector?.regionId) {
      if (v.regionId !== selector.regionId) {
        filterIn = false;
      }
    }

    if (selector?.category) {
      if (v.category !== selector.category.code) {
        filterIn = false;
      }
    }

    if (selector?.personId) {
      if (v.pickupTransport?.personId !== selector.personId && v.deliveryTransport?.personId !== selector?.personId) {
        filterIn = false;
      }
    }

    if (selector?.vehicleId) {
      if (v.pickupTransport?.vehicleId !== selector.vehicleId && v.deliveryTransport?.vehicleId !== selector?.vehicleId) {
        filterIn = false;
      }
    }

    if (selector?.trailerId) {
      if (v.pickupTransport?.trailerId !== selector.trailerId && v.deliveryTransport?.trailerId !== selector?.trailerId) {
        filterIn = false;
      }
    }

    if (selector?.pickupSiteId) {
      if (v.pickupTransport?.siteId !== selector.pickupSiteId) {
        filterIn = false;
      }
    }

    if (selector?.pickupSiteCompanyId) {
      if (v.pickupTransport?.siteCompanyId !== selector.pickupSiteCompanyId) {
        filterIn = false;
      }
    }

    if (selector?.deliverySiteId) {
      if (v.deliveryTransport?.siteId !== selector.deliverySiteId) {
        filterIn = false;
      }
    }

    if (selector?.deliverySiteCompanyId) {
      if (v.deliveryTransport?.siteCompanyId !== selector.deliverySiteCompanyId) {
        filterIn = false;
      }
    }

    if (typeof selector?.adminValidated !== 'undefined') {
      const validated = typeof v.adminValidatedDttm !== 'undefined';
      if (selector.adminValidated !== validated) {
        filterIn = false;
      }
    }

    if (typeof selector?.dispatchValidated !== 'undefined') {
      const validated = typeof v.dispatchValidatedDttm !== 'undefined';
      if (selector.dispatchValidated !== validated) {
        filterIn = false;
      }
    }

    if (typeof selector?.fieldValidated !== 'undefined') {
      const validated = typeof v.fieldValidatedDttm !== 'undefined';
      if (selector.fieldValidated !== validated) {
        filterIn = false;
      }
    }

    if (typeof selector?.businessValidated !== 'undefined') {
      const validated = typeof v.businessValidatedDttm !== 'undefined';
      if (selector.businessValidated !== validated) {
        filterIn = false;
      }
    }

    return filterIn;
  });
};

export const transformTta = (value: TransportAssoc): TransportAssocEntity => {
  return TransportAssocEntity.from(value)
}

export const selectTtasAndTransform = (values?: TransportAssoc[], selector?: TtaSelectorProps): TransportAssocEntity[] => {
  const selected = selectTtas(values, selector);
  return selected.map(transformTta);
};

export const ttaMappedParisSelector = () => createSelector(
  [getUseQueryResultData<TtaMappedPair[]>],
  (data) => {
    return (data ?? []).map(data => TtaMappedPairEntity.from(data));
  }
);
